import { Grid, Text, Table, useMantineTheme, Collapse } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Loader, ScrollArea, ToggleCollapse } from "@tfg/ui"
import _ from "lodash"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { FindOperationChainsResponseDto } from "~/gen/api"
import { TraceabilityRequestItem } from "~/pages/SupplierPortal/TraceabilityRequests/components/TraceabilityRequestItem.tsx"
import { useGetTraceabilityRequest } from "~/pages/SupplierPortal/TraceabilityRequests/hooks.ts"
import { InformationRequestContextProvider } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"
import {
  assignDuplicateProductionSites,
  OperationItem,
  requestsToRequestsWithOp,
  RequestWithOpItem,
} from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import { useAuth } from "~/store/auth-context"

import { InformationRequestSummary } from "./InformationRequestSummary"

interface TraceabilityRequestProps {
  initialData: RequestWithOpItem
  operationChains: FindOperationChainsResponseDto[]
  index: number
  setIrsToSubmit: React.Dispatch<React.SetStateAction<Set<number>>>
}

export const TraceabilityRequest = React.memo(
  ({
    initialData,
    operationChains,
    index,
    setIrsToSubmit,
  }: TraceabilityRequestProps) => {
    const { isAdmin, user } = useAuth()
    const [isOpen, { toggle }] = useDisclosure(index === 0)
    const { t } = useTranslation("traceability")
    const { colors } = useMantineTheme()

    const { data, isPending: isLoading } = useGetTraceabilityRequest(
      initialData.original.id,
      initialData.original
    )

    const request = useMemo(() => {
      if (!isLoading && data) {
        return requestsToRequestsWithOp({
          requests: [data],
          operationChains,
          user,
        })[0]
      } else {
        return initialData
      }
    }, [isLoading, data, operationChains, initialData, user])
    const operationItems = assignDuplicateProductionSites(request.opItems)

    const ranks = Object.keys(request.opItemsByRank)
    const rows = useMemo(() => buildRows(operationItems), [operationItems])

    const requestItems = useMemo(
      () =>
        rows.map((row, index) => (
          <TraceabilityRequestItem
            key={`${request.original.id}-row-${index}`}
            index={index}
            operationsItemWithRowspan={row}
          />
        )),
      [rows, request]
    )

    if (isLoading) return <Loader fullPage />

    return (
      <InformationRequestContextProvider request={request}>
        <InformationRequestSummary setIrsToSubmit={setIrsToSubmit} />
        <ToggleCollapse
          open={isOpen}
          toggle={toggle}
          seeMoreLabel={t("request.seeMore")}
          seeLessLabel={t("request.seeLess")}
        />
        <Collapse in={isOpen}>
          <Grid style={{ border: "1px solid", borderColor: colors.gray[3] }}>
            <Grid.Col span={12} bg="white">
              <ScrollArea
                showEdgeFade
                type="auto"
                w="100%"
                h="60vh"
                zoomable
                hideScrollbar
              >
                <Table
                  style={{
                    borderCollapse: "collapse",
                    border: "none",
                  }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      {ranks.map((rank) => (
                        <Table.Td key={`${request.original.id}-${rank}`} />
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {request.error && isAdmin && (
                      <Table.Tr>
                        <Table.Td>
                          <Text c="violet">{request.error}</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {requestItems}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Grid.Col>
          </Grid>
        </Collapse>
      </InformationRequestContextProvider>
    )
  }
)

function buildRows(operationItems: OperationItem[]) {
  const rootNodes = operationItems.filter((oi) => !oi.parentNodeId)
  if (rootNodes.length !== 1) {
    return []
  }
  const rootNode = rootNodes[0]
  return zipNextRow(rootNode, operationItems)
}

function zipNextRow(
  curnode: OperationItem,
  nodes: OperationItem[]
): ({ op: OperationItem; rowspan: number } | "no-op")[][] {
  const children = nodes.filter((n) => n.parentNodeId === curnode.nodeId)
  if (children.length === 0) {
    return [[{ op: curnode, rowspan: 1 }]]
  }

  const allchild = children.flatMap((child) => zipNextRow(child, nodes))
  const rowspan = allchild.length
  const zipped = _.zip([[{ op: curnode, rowspan }]], allchild)
  return zipped.map((rank) => rank.flatMap((y) => y || ["no-op"]))
}
